export const API_REVISION = '2026-10-15';
export const EVENT_CODE = 'sporefaring_2026';
export const EVENT_NAME = 'Luma Festival Response Submitted';
export const CONSENT_VERSION = 'sporefaring-consent-v4';
export const MARKETING_CONSENT_TEXT = 'Sign up to updates from luma. Unsubscribe anytime.';
export const QUOTE_CONSENT_TEXT = 'Luma may use my comment on its website, with my first name if I add it. My email stays private.';
export const FESTIVAL_REWARDS = Object.freeze({
  signup: Object.freeze({code: 'LUMASPORE10', percentage: 10}),
  feedback: Object.freeze({code: 'LUMASPORE15', percentage: 15})
});
export function festivalReward(hasFeedback) { return hasFeedback ? FESTIVAL_REWARDS.feedback : FESTIVAL_REWARDS.signup; }
export const TASTE_OPTIONS = Object.freeze(['nutty', 'earthy', 'perfectly_dark', 'rich', 'smooth', 'sweet']);

export class FestivalValidationError extends Error {
  constructor(message, field) {
    super(message);
    this.name = 'FestivalValidationError';
    this.field = field;
  }
}

function invalid(message, field) { throw new FestivalValidationError(message, field); }
function text(value, max, field) {
  if (value == null) return '';
  if (typeof value !== 'string') invalid('Please check this answer.', field);
  const result = value.trim();
  if (result.length > max) invalid(`Please use ${max} characters or fewer.`, field);
  return result;
}
function choice(value, choices, field) {
  if (value == null || value === '') return '';
  if (!choices.includes(value)) invalid('Please choose one of the available answers.', field);
  return value;
}

function normalize(input, config) {
  if (!input || typeof input !== 'object') invalid('Please check the form.', 'form');
  const email = text(input.email, 99, 'email');
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) invalid('Please enter a valid email address.', 'email');
  const firstName = input.quoteConsent === true ? text(input.firstName, 60, 'firstName') : '';
  if (typeof input.marketingConsent !== 'boolean' || typeof input.quoteConsent !== 'boolean') {
    invalid('Please check your permission choices.', 'consent');
  }
  if (!/^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(input.submissionId || '')) {
    invalid('Please refresh the page and try again.', 'submissionId');
  }
  const timestamp = Date.parse(input.submittedAt);
  if (typeof input.submittedAt !== 'string' || !Number.isFinite(timestamp) || timestamp < 631152000000 || timestamp > Date.now() + 300000) {
    invalid('Please refresh the page and try again.', 'submittedAt');
  }
  const source = input.feedback;
  if (source != null && (typeof source !== 'object' || Array.isArray(source))) invalid('Please check your feedback.', 'feedback');
  const taste = source?.taste === undefined ? [] : source.taste;
  if (!Array.isArray(taste) || taste.length > 6 || new Set(taste).size !== taste.length || Array.from(taste).some(value => !TASTE_OPTIONS.includes(value))) {
    invalid('Please choose each flavour at most once from the available options.', 'taste');
  }
  const feedback = {
    product: choice(source?.product, ['DAWN', 'DAY', 'DUSK', 'NOT_SURE'], 'product'),
    reaction: choice(source?.reaction, ['love', 'okay', 'not_for_me'], 'reaction'),
    taste: [...taste],
    buyAgain: choice(source?.buyAgain, ['yes', 'maybe', 'no'], 'buyAgain'),
    comment: text(source?.comment, 1000, 'comment')
  };
  const hasFeedback = Boolean(feedback.reaction || feedback.taste.length || feedback.buyAgain || feedback.comment);
  if (!hasFeedback && !input.marketingConsent) invalid('Leave some feedback or choose to receive Luma emails.', 'form');
  if (input.quoteConsent && !feedback.comment) invalid('Add your own words before allowing us to quote them.', 'quoteConsent');
  if (!config || !/^[A-Za-z0-9]{6}$/.test(config.siteId || '')) invalid('This form is not connected yet. Please ask the Luma team.', 'config');
  if (input.marketingConsent && !/^[A-Za-z0-9]{6}$/.test(config.listId || '')) invalid('Email signup is not connected yet. Please ask the Luma team.', 'config');
  const revision = config.revision || API_REVISION;
  if (revision !== API_REVISION) invalid('The form API revision needs updating.', 'config');
  return {
    email, firstName, feedback, hasFeedback,
    marketingConsent: input.marketingConsent, quoteConsent: input.quoteConsent,
    submissionId: input.submissionId,
    submittedAt: new Date(timestamp).toISOString(),
    siteId: config.siteId, listId: input.marketingConsent ? config.listId : '', revision
  };
}

function eventPayload(record) {
  const feedback = record.feedback;
  const properties = {
    event_code: EVENT_CODE,
    source: 'Luma Sporefaring festival page',
    schema_version: '3',
    reward_code: festivalReward(record.hasFeedback).code,
    reward_percentage: festivalReward(record.hasFeedback).percentage,
    feedback_incentive: record.hasFeedback ? '15_percent_off_for_any_honest_feedback' : 'none',
    response_id: record.submissionId,
    submitted_at: record.submittedAt,
    has_feedback: record.hasFeedback ? 'yes' : 'no',
    marketing_opt_in: record.marketingConsent ? 'yes' : 'no',
    quote_permission: record.quoteConsent ? 'yes' : 'no',
    consent_version: CONSENT_VERSION,
    marketing_consent_text: MARKETING_CONSENT_TEXT,
    quote_consent_text: QUOTE_CONSENT_TEXT,
    ...(record.firstName ? { attribution_first_name: record.firstName } : {}),
    ...(feedback.product ? { product: feedback.product } : {}),
    ...(feedback.reaction ? { reaction: feedback.reaction } : {}),
    ...(feedback.taste.length ? { taste: [...feedback.taste] } : {}),
    ...(feedback.buyAgain ? { buy_again: feedback.buyAgain } : {}),
    ...(feedback.comment ? { comment: feedback.comment } : {})
  };
  return { data: { type: 'event', attributes: {
    properties,
    time: record.submittedAt,
    unique_id: record.submissionId,
    metric: { data: { type: 'metric', attributes: { name: EVENT_NAME } } },
    profile: { data: { type: 'profile', attributes: { email: record.email } } }
  } } };
}

function subscriptionPayload(record) {
  return { data: {
    type: 'subscription',
    attributes: {
      custom_source: `Luma Sporefaring festival page · ${CONSENT_VERSION}`,
      profile: { data: { type: 'profile', attributes: {
        email: record.email,
        subscriptions: { email: { marketing: { consent: 'SUBSCRIBED' } } },
        properties: { luma_festival_signup_source: EVENT_CODE, luma_festival_consent_version: CONSENT_VERSION }
      } } }
    },
    relationships: { list: { data: { type: 'list', id: record.listId } } }
  } };
}

function retryAfter(response) {
  const raw = response.headers?.get?.('Retry-After');
  if (!raw) return undefined;
  const seconds = Number(raw);
  if (Number.isFinite(seconds)) return Math.max(0, Math.ceil(seconds));
  const date = Date.parse(raw);
  return Number.isFinite(date) ? Math.max(0, Math.ceil((date - Date.now()) / 1000)) : undefined;
}

async function post(endpoint, payload, record, fetchImpl, timeoutMs) {
  const controller = new AbortController();
  let timedOut = false;
  const timer = setTimeout(() => { timedOut = true; controller.abort(); }, timeoutMs);
  try {
    const response = await fetchImpl(`https://a.klaviyo.com/client/${endpoint}?company_id=${encodeURIComponent(record.siteId)}`, {
      method: 'POST', mode: 'cors', credentials: 'omit', cache: 'no-store',
      headers: { 'Content-Type': 'application/vnd.api+json', accept: 'application/vnd.api+json', revision: record.revision },
      signal: controller.signal,
      body: JSON.stringify(payload)
    });
    if (response.status === 202) return { status: 'accepted' };
    return {
      status: 'failed', code: `http_${response.status}`,
      retryable: response.status === 429 || response.status >= 500,
      ...(response.status === 429 && retryAfter(response) !== undefined ? { retryAfterSeconds: retryAfter(response) } : {})
    };
  } catch {
    return { status: 'failed', code: timedOut ? 'timeout' : 'network', retryable: true };
  } finally { clearTimeout(timer); }
}


export async function submitFestivalResponse(input, config, { fetchImpl = globalThis.fetch, priorResult, timeoutMs = 12000 } = {}) {
  const record = normalize(input, config);
  if (typeof fetchImpl !== 'function') invalid('Your browser cannot send this form. Please ask the Luma team.', 'browser');
  if (!Number.isInteger(timeoutMs) || timeoutMs < 1000 || timeoutMs > 30000) invalid('Please refresh the page and try again.', 'timeoutMs');
  const digest = await globalThis.crypto.subtle.digest('SHA-256', new TextEncoder().encode(JSON.stringify(record)));
  const fingerprint = Array.from(new Uint8Array(digest), byte => byte.toString(16).padStart(2, '0')).join('');
  if (priorResult && (priorResult.submissionId !== record.submissionId || priorResult.fingerprint !== fingerprint)) {
    invalid('Retry the original response. Start a new response to change your answers.', 'retry');
  }
  const event = priorResult?.event?.status === 'accepted'
    ? { status: 'accepted' }
    : await post('events', eventPayload(record), record, fetchImpl, timeoutMs);
  const subscription = !record.marketingConsent
    ? { status: 'skipped' }
    : priorResult?.subscription?.status === 'accepted'
      ? { status: 'accepted' }
      : await post('subscriptions', subscriptionPayload(record), record, fetchImpl, timeoutMs);
  const failures = [event, subscription].filter(item => item.status === 'failed');
  const status = failures.length === 0 ? 'complete'
    : [event, subscription].some(item => item.status === 'accepted') ? 'partial' : 'failed';
  return Object.freeze({
    submissionId: record.submissionId, fingerprint, status,
    hasFeedback: record.hasFeedback,
    event: Object.freeze(event), subscription: Object.freeze(subscription)
  });
}


export function mountFestival(root, config, { fetchImpl } = {}) {
  if (!root || root.dataset.mounted) return;
  root.dataset.mounted = 'true';
  const form = root.querySelector('form');
  const fields = root.querySelector('[data-form-fields]');
  const button = form.querySelector('[type=submit]');
  const buttonLabel = root.querySelector('[data-button-label]');
  const error = root.querySelector('[data-form-error]');
  const emailError = root.querySelector('#email-error');
  const success = root.querySelector('[data-success]');
  const controls = form.elements;
  let issuedReward;
  const copyButton = root.querySelector('[data-copy-code]');
  copyButton.addEventListener('click', async () => {
    if (!issuedReward) return;
    const status = root.querySelector('[data-copy-status]');
    try {
      await navigator.clipboard.writeText(issuedReward.code);
      copyButton.dataset.copied = 'true';
      copyButton.setAttribute('aria-label', 'Discount code copied');
      status.textContent = '';
      status.hidden = true;
    } catch {
      delete copyButton.dataset.copied;
      copyButton.setAttribute('aria-label', 'Copy discount code');
      status.textContent = 'Select the code above to copy it.';
      status.hidden = false;
    }
  });
  let frozenInput;
  let priorResult;
  let busy = false;
  let retryAt = 0;
  let retryTimer;
  const startedAt = Date.now();

  root.querySelector('[data-marketing-copy]').textContent = MARKETING_CONSENT_TEXT;
  root.querySelector('[data-quote-copy]').textContent = QUOTE_CONSENT_TEXT;

  function scheduleMeadowCelebration() {
    const meadow = root.querySelector('[data-meadow]');
    const document = root.ownerDocument;
    const view = document.defaultView;
    const reducedMotion = view?.matchMedia('(prefers-reduced-motion: reduce)');
    if (!meadow || !view || reducedMotion.matches || meadow.dataset.celebrate) return;
    let observer;
    let visible = false;
    let disposed = false;
    function cleanup() {
      disposed = true;
      observer?.disconnect();
      view.removeEventListener('pagehide', cleanup);
      document.removeEventListener('visibilitychange', start);
      document.removeEventListener('shopify:section:unload', unload);
    }
    function start() {
      if (disposed || !visible || document.hidden) return;
      cleanup();
      if (meadow.isConnected && !reducedMotion.matches) meadow.dataset.celebrate = 'true';
    }
    function unload(event) {
      if (event.target === root || event.target?.contains?.(root)) cleanup();
    }
    view.addEventListener('pagehide', cleanup, { once: true });
    document.addEventListener('visibilitychange', start);
    document.addEventListener('shopify:section:unload', unload);
    if (typeof view.IntersectionObserver === 'function') {
      observer = new view.IntersectionObserver(entries => {
        visible = entries.some(entry => entry.isIntersecting && entry.intersectionRatio >= .2);
        start();
      }, { threshold: .2 });
      observer.observe(meadow);
    } else {
      visible = true;
      start();
    }
  }

  function values() {
    const data = new FormData(form);
    return {
      email: String(data.get('email') || '').trim(),
      firstName: controls.quoteConsent.checked ? String(data.get('firstName') || '').trim() : '',
      marketingConsent: true,
      quoteConsent: controls.quoteConsent.checked && Boolean(controls.comment.value.trim()),
      feedback: {
        product: String(data.get('product') || ''),
        reaction: String(data.get('reaction') || ''),
        taste: data.getAll('taste').map(String),
        buyAgain: String(data.get('buyAgain') || ''),
        comment: String(data.get('comment') || '').trim()
      }
    };
  }
  function hasFeedback(input) {
    const f = input.feedback;
    return Boolean(f.reaction || f.taste.length || f.buyAgain || f.comment);
  }
  function sync() {
    if (frozenInput || busy) return;
    const hasComment = Boolean(controls.comment.value.trim());
    root.querySelector('[data-quote-fields]').hidden = !hasComment;
    if (!hasComment) controls.quoteConsent.checked = false;
    root.querySelector('[data-name-field]').hidden = !controls.quoteConsent.checked;
    if (!controls.quoteConsent.checked) controls.firstName.value = '';
    const input = values();
    buttonLabel.textContent = hasFeedback(input) ? 'Sign me up & send feedback' : 'Sign me up';
    error.hidden = true;
  }
  form.addEventListener('input', () => {
    sync();
    controls.email.removeAttribute('aria-invalid');
    emailError.hidden = true;
  });
  form.addEventListener('change', sync);
  function showError(message) {
    error.textContent = message;
    error.hidden = false;
    error.focus();
  }
  function waitToRetry(seconds) {
    retryAt = Date.now() + seconds * 1000;
    clearInterval(retryTimer);
    function tick() {
      const remaining = Math.max(0, Math.ceil((retryAt - Date.now()) / 1000));
      button.disabled = remaining > 0;
      buttonLabel.textContent = remaining > 0 ? `Try again in ${remaining}s` : 'Try sending again';
      if (!remaining) clearInterval(retryTimer);
    }
    tick();
    if (seconds) retryTimer = setInterval(tick, 1000);
  }
  form.addEventListener('submit', async event => {
    event.preventDefault();
    if (busy || Date.now() < retryAt) return;
    error.hidden = true;
    if (!frozenInput) {
      if (!controls.email.validity.valid || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(controls.email.value.trim())) {
        emailError.textContent = 'Please enter a valid email address.';
        emailError.hidden = false;
        controls.email.setAttribute('aria-invalid', 'true');
        controls.email.focus();
        return;
      }
      const input = values();
      if (controls.website.value || Date.now() - startedAt < 1000) {
        showError('Please wait a moment, then try again.');
        return;
      }
      if (!config.enabled || config.siteId !== 'Xn96Y7') {
        showError('This form is not open yet. Please speak to Janina at the stand.');
        return;
      }
      if (!globalThis.crypto?.randomUUID || !globalThis.crypto?.subtle) {
        showError('Please open this page in an up-to-date browser to send your feedback.');
        return;
      }
      frozenInput = { ...input, submissionId: crypto.randomUUID(), submittedAt: new Date().toISOString() };
    }
    busy = true;
    fields.disabled = true;
    button.disabled = true;
    buttonLabel.textContent = 'Sending…';
    form.setAttribute('aria-busy', 'true');
    try {
      priorResult = await submitFestivalResponse(frozenInput, config, { fetchImpl, priorResult });
      if (priorResult.status === 'complete') {
        form.hidden = true;
        success.hidden = false;
        issuedReward = festivalReward(priorResult.hasFeedback);
        root.querySelector('[data-reward-heading]').textContent = `Your ${issuedReward.percentage}% off, to take home.`;
        root.querySelector('[data-reward-code]').textContent = issuedReward.code;
        root.querySelector('[data-reward]').hidden = false;
        root.querySelector('[data-reward-link]').href = `https://lumamushroom.co.uk/discount/${issuedReward.code}?redirect=/collections/all`;
        const copy = [];
          if (priorResult.hasFeedback) copy.push('Your feedback has been sent. Thank you for sharing your thoughts.');
          if (frozenInput.marketingConsent) copy.push(config.doubleOptIn
            ? 'Please check your inbox for a confirmation email to finish joining the list. If you already subscribe, you’re all set.'
            : 'Your request to receive Luma emails has been sent.');
        const successCopy = root.querySelector('[data-success-copy]');
        successCopy.textContent = copy.join(' ');
        successCopy.hidden = copy.length === 0;
        scheduleMeadowCelebration();
        success.focus();
        frozenInput = null;
        priorResult = null;
        form.reset();
        return;
      }
      const failures = [priorResult.event, priorResult.subscription].filter(item => item.status === 'failed');
      const canRetry = failures.every(item => item.retryable);
      let message = 'We couldn’t confirm your submission.';
      if (priorResult.event.status === 'accepted') message = 'Your response has been sent, but the email signup hasn’t gone through.';
      else if (priorResult.subscription.status === 'accepted') message = priorResult.hasFeedback
        ? 'Your email signup request has been sent, but we couldn’t confirm your feedback.'
        : 'Your email signup request has been sent, but we couldn’t confirm the rest of your response.';
      showError(message + (canRetry ? ' Your answers are kept here. Try sending again without refreshing this page.' : ' Please speak to Janina at the stand.'));
      if (canRetry) waitToRetry(Math.max(0, ...failures.map(item => item.retryAfterSeconds || 0)));
      else { button.disabled = true; buttonLabel.textContent = 'Please speak to Janina'; }
    } catch (cause) {
      if (cause.name === 'FestivalValidationError') {
        frozenInput = null;
        priorResult = null;
        fields.disabled = false;
        button.disabled = false;
        busy = false;
        sync();
        showError(cause.message);
      } else {
        showError('We couldn’t confirm your submission. Your answers are kept here. Please try again without refreshing.');
        waitToRetry(0);
      }
    } finally {
      busy = false;
      form.removeAttribute('aria-busy');
    }
  });
  fields.disabled = false;
  button.disabled = false;
  root.querySelector("[data-boot-message]").hidden = true;
  sync();
}

function bootFestival(scope = document) {
  scope.querySelectorAll('[data-festival]').forEach(root => mountFestival(root, {
    siteId: root.dataset.siteId,
    listId: root.dataset.listId,
    enabled: root.dataset.enabled === 'true',
    doubleOptIn: root.dataset.doubleOptIn === 'true'
  }));
}
bootFestival();
document.addEventListener('shopify:section:load', event => bootFestival(event.target));
